import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';
import {
  isBannerDismissed,
  isBannerLive,
  markBannerDismissed,
  type PublicBanner,
} from '../types/banner';
import { getPublicBanner } from '../services/bannerApi';

export type BannerSource = 'fetch' | 'socket' | 'expire' | null;

interface BannerSliceState {
  banner: PublicBanner | null;
  modalOpen: boolean;
  loading: boolean;
  error: string | null;
  lastSource: BannerSource;
  lastSyncedAt: number | null;
}

const initialState: BannerSliceState = {
  banner: null,
  modalOpen: false,
  loading: false,
  error: null,
  lastSource: null,
  lastSyncedAt: null,
};

function resolveBanner(banner: PublicBanner | null): Pick<BannerSliceState, 'banner' | 'modalOpen'> {
  if (!banner || !isBannerLive(banner)) {
    return { banner: null, modalOpen: false };
  }
  return {
    banner,
    modalOpen: !isBannerDismissed(banner.id),
  };
}

export const fetchBanner = createAsyncThunk(
  'banner/fetch',
  async (_: void, { rejectWithValue }) => {
    try {
      return await getPublicBanner();
    } catch (err: any) {
      return rejectWithValue(err?.message ?? 'Не удалось загрузить объявление');
    }
  },
);

const bannerSlice = createSlice({
  name: 'banner',
  initialState,
  reducers: {
    applyBanner: (
      state,
      action: PayloadAction<{ banner: PublicBanner | null; source?: BannerSource }>,
    ) => {
      const next = resolveBanner(action.payload.banner);
      state.banner = next.banner;
      state.modalOpen = next.modalOpen;
      state.lastSource = action.payload.source ?? 'socket';
      state.lastSyncedAt = Date.now();
      state.error = null;
    },
    dismissBannerModal: (state) => {
      if (!state.banner) return;
      markBannerDismissed(state.banner.id);
      state.modalOpen = false;
    },
    openBannerModal: (state) => {
      if (!state.banner || !isBannerLive(state.banner)) return;
      state.modalOpen = true;
    },
    expireBanner: (state) => {
      state.banner = null;
      state.modalOpen = false;
      state.lastSource = 'expire';
      state.lastSyncedAt = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBanner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBanner.fulfilled, (state, action) => {
        const next = resolveBanner(action.payload);
        state.banner = next.banner;
        state.modalOpen = next.modalOpen;
        state.loading = false;
        state.lastSource = 'fetch';
        state.lastSyncedAt = Date.now();
      })
      .addCase(fetchBanner.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Не удалось загрузить объявление';
      });
  },
});

export const { applyBanner, dismissBannerModal, openBannerModal, expireBanner } = bannerSlice.actions;

export const selectBannerState = (state: RootState) => state.banner;
export const selectActiveBanner = (state: RootState) => state.banner.banner;
export const selectBannerModalOpen = (state: RootState) => state.banner.modalOpen;

export default bannerSlice.reducer;
